import axios, { AxiosResponse } from 'axios';
import {
  ValidationResult,
  PagedResponse,
  Order,
  Product,
  ServiceType,
  Make,
  Model,
  Technician,
  TechnicianPerformance,
  Lead,
  LeadFollowUp,
  DashboardStats,
  OrderQueryRequest,
  OrderStatusUpdateRequest,
} from '../types';

class ApiService {
  //private baseURL = 'https://localhost:53857/api'; // Match Angular configuration
   private baseURL = 'https://cij3b8xauj.execute-api.ap-south-1.amazonaws.com/Prod/api';

  private api = axios.create({
    baseURL: this.baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('isLoggedIn');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Products
  async getProducts(page: number = 1, pageSize: number = 10, search: string = ''): Promise<ValidationResult<any>> {
    const response = await this.api.get('/v1/Model/products', {
      params: { page, pageSize, search }
    });
    return response.data;
  }

  async getProduct(id: number): Promise<ValidationResult<Product>> {
    const response = await this.api.get(`/v1/Model/products/${id}`);
    return response.data;
  }

  async updateProduct(id: number, product: FormData): Promise<ValidationResult<Product>> {
    const response = await this.api.post(`/v1/Model/products/${id}`, product, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async createProduct(product: FormData): Promise<ValidationResult<Product>> {
    const response = await this.api.post(`/v1/Model/products`, product, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteProduct(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/Model/products/${id}`);
    return response.data;
  }

  // Makes
  async getMakes(): Promise<ValidationResult<Make[]>> {
    const response = await this.api.get('/v1/Make/makes');
    return response.data;
  }

  // Models
  async getModels(makeId: number): Promise<ValidationResult<Model[]>> {
    const response = await this.api.get(`/v1/model/${makeId}/models`);
    return response.data;
  }

  // Service Types
  async getServiceTypes(modelId: number): Promise<ValidationResult<ServiceType[]>> {
    const response = await this.api.get(`/v1/model/${modelId}/servicetypes`);
    return response.data;
  }

  // Orders - matching Angular API exactly
  async searchOrders(
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = '',
    startDate?: Date,
    endDate?: Date,
    status?: string
  ): Promise<ValidationResult<PagedResponse<Order>>> {
    const params: any = {
      page: page.toString(),
      pageSize: pageSize.toString()
    };
    
    if (searchTerm) params.searchTerm = searchTerm;
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (status) params.status = status;

    const response = await this.api.get('/v1/Order/search', { params });
    return response.data;
  }

  async getOrders(queryRequest: OrderQueryRequest): Promise<ValidationResult<PagedResponse<Order>>> {
    const params: any = {
      page: queryRequest.page,
      pageSize: queryRequest.pageSize
    };
    
    if (queryRequest.search) params.searchTerm = queryRequest.search;
    if (queryRequest.status) params.status = queryRequest.status;
    if (queryRequest.startDate) params.startDate = queryRequest.startDate;
    if (queryRequest.endDate) params.endDate = queryRequest.endDate;

    const response = await this.api.get('/v1/Order/search', { params });
    return response.data;
  }

  async getOrder(id: number): Promise<ValidationResult<Order>> {
    const response = await this.api.get(`/v1/Order/${id}`);
    return response.data;
  }

  async createOrder(order: Order): Promise<ValidationResult<Order>> {
    const response = await this.api.post('/v1/Order', order);
    return response.data;
  }

  async updateOrder(id: number, order: Order): Promise<ValidationResult<Order>> {
    const response = await this.api.put(`/v1/Order/${id}`, order);
    return response.data;
  }

  async deleteOrder(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/Order/${id}`);
    return response.data;
  }

  async updateOrderStatus(orderId: number, request: OrderStatusUpdateRequest): Promise<ValidationResult<void>> {
    const response = await this.api.put(`/v1/Order/${orderId}/status`, request);
    return response.data;
  }

  async assignTechnicianToOrder(orderId: number, technicianId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Technician/orders/${orderId}/assign/${technicianId}`, {});
    return response.data;
  }

  async getOrdersByCustomer(mobile: string): Promise<ValidationResult<Order[]>> {
    const response = await this.api.get(`/v1/Order/customer/${mobile}`);
    return response.data;
  }

  async addOrderPayment(id: number, payment: { orderId: number; paymentMethod: string; paymentAmount: number; transactionId?: string }): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Order/${id}/payment`, payment);
    return response.data;
  }

  async getOrderCount(): Promise<ValidationResult<number>> {
    const response = await this.api.get('/v1/Order/count');
    return response.data;
  }

  async sendOrderDetails(orderId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/order/${orderId}/SendOrderDetails`, {});
    return response.data;
  }

  // Dashboard
  async getDashboardStats(startDate?: string, endDate?: string, technicianId?: number | null): Promise<ValidationResult<DashboardStats>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (technicianId) params.technicianId = technicianId.toString();

    const response = await this.api.get('/v1/dashboard/stats', { params });
    return response.data;
  }

  // Technicians
  async getTechnicians(): Promise<ValidationResult<Technician[]>> {
    const response = await this.api.get('/v1/Technician');
    return response.data;
  }

  async getTechnician(id: number): Promise<ValidationResult<Technician>> {
    const response = await this.api.get(`/v1/Technician/${id}`);
    return response.data;
  }

  async createTechnician(technician: FormData): Promise<ValidationResult<Technician>> {
    const response = await this.api.post('/v1/Technician', technician, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateTechnician(id: number, technician: FormData): Promise<ValidationResult<Technician>> {
    const response = await this.api.put(`/v1/Technician/${id}`, technician, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteTechnician(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/technician/${id}`);
    return response.data;
  }

  async getTechnicianPerformance(id: number): Promise<ValidationResult<TechnicianPerformance>> {
    const response = await this.api.get(`/v1/technician/${id}/performance`);
    return response.data;
  }

  async getTechnicianWorkload(technicianId: number): Promise<ValidationResult<Order[]>> {
    const response = await this.api.get(`/v1/technician/${technicianId}/workload`);
    return response.data;
  }

  async uploadTechnicianPhoto(file: File): Promise<ValidationResult<string>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/v1/Technician/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Leads
  async getLeads(page: number = 1, pageSize: number = 10, search: string = '', status?: string, startDate?: string, endDate?: string): Promise<ValidationResult<PagedResponse<Lead>>> {
    const params: any = { page, pageSize, search };
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/v1/leads', { params });
    return response.data;
  }

  async searchLeads(
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = '',
    startDate?: Date,
    endDate?: Date,
    status?: string,
    source?: string,
    priority?: string
  ): Promise<ValidationResult<PagedResponse<Lead>>> {
    const params: any = { page, pageSize };
    if (searchTerm) params.searchTerm = searchTerm;
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (status) params.status = status;
    if (source) params.source = source;
    if (priority) params.priority = priority;

    const response = await this.api.get('/v1/leads/search', { params });
    return response.data;
  }

  async getLead(id: number): Promise<ValidationResult<Lead>> {
    const response = await this.api.get(`/v1/leads/${id}`);
    return response.data;
  }

  async createLead(lead: Lead): Promise<ValidationResult<Lead>> {
    const response = await this.api.post('/v1/leads', lead);
    return response.data;
  }

  async updateLead(id: number, lead: Lead): Promise<ValidationResult<Lead>> {
    const response = await this.api.put(`/v1/leads/${id}`, lead);
    return response.data;
  }

  async deleteLead(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/leads/${id}`);
    return response.data;
  }

  async updateLeadStatus(id: number, status: string): Promise<ValidationResult<void>> {
    const response = await this.api.put(`/v1/leads/${id}/status`, { status });
    return response.data;
  }

  async assignLead(id: number, technicianId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/leads/${id}/assign`, { technicianId });
    return response.data;
  }

  // Lead Follow-ups
  async getLeadFollowUps(leadId: number): Promise<ValidationResult<LeadFollowUp[]>> {
    const response = await this.api.get(`/v1/leads/${leadId}/followups`);
    return response.data;
  }

  async createLeadFollowUp(leadId: number, followUp: LeadFollowUp): Promise<ValidationResult<LeadFollowUp>> {
    const response = await this.api.post(`/v1/leads/followups`, followUp);
    return response.data;
  }

  async updateLeadFollowUp(leadId: number, followUpId: number, followUp: LeadFollowUp): Promise<ValidationResult<LeadFollowUp>> {
    const response = await this.api.put(`/v1/leads/followups/${followUpId}`, followUp);
    return response.data;
  }

  // Content Creator
  async generateContent(prompt: string): Promise<ValidationResult<{ content: string }>> {
    const params = new URLSearchParams();
    params.set('content', prompt);
    const response = await this.api.get(`/contentwriter/create?${params}`);
    return response.data;
  }

  // Order Feedback
  async getOrderFeedback(orderId: number): Promise<ValidationResult<any>> {
    const response = await this.api.get(`/v1/OrderFeedback/${orderId}`);
    return response.data;
  }

  async createOrderFeedback(formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.post('/v1/OrderFeedback', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateOrderFeedback(id: number, formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.put(`/v1/OrderFeedback/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async createOrderFeedbackWithPhoto(formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.post('/v1/OrderFeedback/with-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Customers
  async getCustomers(page: number = 1, pageSize: number = 10, search: string = ''): Promise<ValidationResult<any>> {
    const response = await this.api.get('/v1/customers', {
      params: { page, pageSize, search }
    });
    return response.data;
  }

  async getCustomer(id: number): Promise<ValidationResult<any>> {
    const response = await this.api.get(`/v1/customers/${id}`);
    return response.data;
  }

  // Utility methods
  getFullImageUrl(imagePath: string): string {
    const baseImageUrl = 'http://admin.mintomein.in/public/uploads/';
    return `${baseImageUrl}${imagePath}`;
  }

  // Error handling helper
  handleError(error: any): string {
    if (error.response?.data?.errors?.length > 0) {
      return error.response.data.errors.join(', ');
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  }
}

export const apiService = new ApiService();
export default apiService; 